import React, { useContext, useEffect, useState } from 'react';
import { Button, CircularProgress, Grid, MenuItem, Select } from '@material-ui/core';

import { fbmApi as api } from 'api/fbm';
import { warehouseApi } from 'api/warehouse';
import { UserContext } from 'contexts/UserContext';
import { DataTable, FBMAccountPicker, Notifications } from 'shared';

import { FBMItemLotDetails } from './FBMInventoryLotDetails';

function FBMInventory() {
    const [rows, setRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [facilityID, setFacilityID] = useState(null);
    const { fbmAccount, user } = useContext(UserContext);
    const [loading, setLoading] = useState(false);
    const [loadingUnits, setLoadingUnits] = useState(false);
    const [facilities, setFacilities] = useState([]);
    const [lastFbmAccountId, setLastFbmAccountId] = useState(null);

    // Fetch facilities for the custom picker
    const getFacilities = async () => {
        try {
            const res = await warehouseApi.getFacilities(true);
            if (res.status !== true) {
                return;
            }

            let fac = res.data.rows.map((item) => ({
                text: item.name,
                value: item.id,
                active: item.active,
            }));

            if (fbmAccount?.facilityIDs?.length > 0) {
                fac = fac.filter((item) => fbmAccount.facilityIDs.includes(item.value));
            }

            const facilitiesWithAll = [{ text: 'ALL FACILITIES', value: 'ALL', active: true }, ...fac];

            setFacilities(facilitiesWithAll);
        } catch (error) {
            console.error('Error fetching facilities:', error);
        }
    };

    const getRowsForFacility = async (currentFacilityID) => {
        const inventoryResponse = await api.getInventory(currentFacilityID, fbmAccount.fbmAccountName);
        if (!inventoryResponse.status) {
            setNotification({ text: `Failed to get inventory for facility: ${inventoryResponse.msg}`, level: 'error' });
            return [];
        }

        const mappedRows = (inventoryResponse.data?.rows ?? []).map((row) => ({
            ...row,
            risk: handleAtRisk(row.numUnits, row.inventoryRiskThreshold),
            facilityID: currentFacilityID,
        }));

        const externalSkus = [];
        const packagingIds = [];
        mappedRows.forEach((row) => {
            if (row.type === 'Item' && row.externalSku) {
                externalSkus.push(row.externalSku);
            } else if (row.type === 'Packaging' && row.packagingId) {
                packagingIds.push(row.packagingId);
            }
        });

        const unitsResponse = await api.getInventoryUnitsandLotDetails(
            currentFacilityID,
            fbmAccount.fbmAccountName,
            externalSkus,
            packagingIds,
        );

        if (!unitsResponse.status) {
            throw new Error(`Failed to get inventory units for facility ${currentFacilityID}: ${unitsResponse.msg}`);
        }

        const unitResponseById = new Map(unitsResponse.data.units.map((unit) => [unit.id, unit]));

        const updatedRows = mappedRows.map((row) => {
            const unitData = unitResponseById.get(row.id);

            if (unitData) {
                const updatedRow = {
                    ...row,
                    onHandUnits: unitData.onHandUnits,
                    onOrderUnits: unitData.onOrderUnits,
                    qcUnits: unitData.qcUnits,
                    availableUnits: unitData.availableUnits,
                    lotDetails: unitData.lotDetails || [],
                    risk: handleAtRisk(unitData.availableUnits, row.inventoryRiskThreshold),
                    facilityName: facilities.find((facility) => facility.value === currentFacilityID)?.text,
                };
                return updatedRow;
            }
            return {
                ...row,
                lotDetails: [],
                facilityName: facilities.find((facility) => facility.value === currentFacilityID)?.text,
            };
        });

        return updatedRows;
    };

    // Load rows
    const getRows = async () => {
        setLoading(true);
        setLoadingUnits(true);
        try {
            if (facilityID === 'ALL') {
                // Fetch data for all facilities
                const facilityPromises = fbmAccount.facilityIDs.map((currentFacilityID) =>
                    getRowsForFacility(currentFacilityID),
                );

                const allFacilityResults = await Promise.all(facilityPromises);
                const combinedRows = allFacilityResults.flat(); // Flatten the array of arrays
                setRows(combinedRows);
            } else {
                // Fetch data for single facility
                const facilityRows = await getRowsForFacility(facilityID);
                setRows(facilityRows);
            }
        } catch (error) {
            setNotification({ text: `Error fetching inventory data: ${error.message}`, level: 'error' });
            setRows([]);
        } finally {
            setLoading(false);
            setLoadingUnits(false);
        }
    };

    useEffect(() => {
        if (!!user.id && user.facilityID) {
            setFacilityID(user.facilityID);
        }
    }, [user]);

    useEffect(() => {
        if (fbmAccount.id) {
            getFacilities();

            // Check if we're switching FBM accounts. If so, set the facilityID to 'ALL'.
            if (lastFbmAccountId !== fbmAccount.id) {
                setLastFbmAccountId(fbmAccount.id);
                setFacilityID('ALL');
            }
        }
    }, [fbmAccount, lastFbmAccountId]);

    useEffect(() => {
        // Only make API calls when both facilityID and fbmAccount are available
        // and when facilities have been loaded
        if (!!facilityID && !!fbmAccount.id && facilities.length > 0) {
            getRows();
        }
    }, [facilityID, fbmAccount, facilities]);

    const handleAtRisk = (numUnits, inventoryRiskThreshold) => {
        if (!inventoryRiskThreshold) {
            return '';
        }

        if (numUnits <= inventoryRiskThreshold) {
            return 'AT RISK';
        }

        return '';
    };

    const exportDetailedData = () => {
        if (!rows || rows.length === 0) {
            setNotification({ text: 'No data to export', level: 'warning' });
            return;
        }

        // Build header row with all V2 fields plus lot details
        let csvContent =
            'Facility,Item ID,Type,Name,Risk Threshold,Risk,On-Hand Units,Order Allocated Units,QC Audit Units,Available Units,Status';

        // Add lot detail headers
        csvContent += ',Lot Number,Partner Lot,Received On,Expires On,Lot Units';
        csvContent += '\r\n';

        for (const row of rows) {
            const rowData = row;
            const lotDetails = rowData?.lotDetails ?? [];

            // Base row data with lazy-loaded fields
            const baseRowData = [
                rowData.facilityName || '',
                rowData.id || '',
                rowData.type || '',
                `"${(rowData.name || '').replace(/"/g, '""')}"`,
                rowData.inventoryRiskThreshold ?? '',
                rowData.risk || '',
                rowData.onHandUnits ?? 0,
                rowData.onOrderUnits ?? 0,
                rowData.qcUnits ?? 0,
                rowData.availableUnits ?? 0,
            ];

            // If there are lot details, export one row per lot
            if (lotDetails && Array.isArray(lotDetails) && lotDetails.length > 0) {
                for (const lotDetailsRow of lotDetails) {
                    const lotRowData = [
                        lotDetailsRow.status || '',
                        lotDetailsRow.lotID || '',
                        lotDetailsRow.partnerLotNumber ? lotDetailsRow.partnerLotNumber.replace(',', '') : '',
                        lotDetailsRow.receivedAt || '',
                        lotDetailsRow.expiresOn || '',
                        lotDetailsRow.numUnits ?? 0,
                    ];

                    csvContent += `${baseRowData.join(',')},${lotRowData.join(',')}\r\n`;
                }
            } else {
                // No lot details, just export the main row with empty lot fields
                const emptyLotData = ['', '', '', '', '', ''];
                csvContent += `${baseRowData.join(',')},${emptyLotData.join(',')}\r\n`;
            }
        }

        const filename = `inventory_detailed.csv`;
        const file = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        if (window.navigator.msSaveOrOpenBlob)
            // IE10+
            window.navigator.msSaveOrOpenBlob(file, filename);
        else {
            // Others
            const a = document.createElement('a');
            const url = URL.createObjectURL(file);
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                if (window.URL && typeof window.URL.revokeObjectURL === 'function') {
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                }
            }, 0);
        }
    };

    const cols = [
        { accessor: 'facilityName', Header: 'Facility' },
        { accessor: 'id', Header: 'Item ID' },
        { accessor: 'type', Header: 'Type' },
        { accessor: 'name', Header: 'Name' },
        {
            accessor: 'inventoryRiskThreshold',
            Header: 'Risk Threshold',
            editable: true,
            editProps: { type: 'input', inputType: 'number', min: 0, integer: true },
        },
        {
            accessor: 'risk',
            Header: 'Risk',
            Cell: ({ row, value }) =>
                loadingUnits ? (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={20} />
                    </div>
                ) : (
                    (row.original.risk ?? '')
                ),
        },
        {
            Header: 'On-Hand Units',
            accessor: 'onHandUnits',
            Cell: ({ row, value }) =>
                loadingUnits ? (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={20} />
                    </div>
                ) : (
                    (row.original.onHandUnits ?? 0)
                ),
        },
        {
            Header: 'Order Allocated Units',
            accessor: 'onOrderUnits',
            Cell: ({ row, value }) =>
                loadingUnits ? (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={20} />
                    </div>
                ) : (
                    (row.original.onOrderUnits ?? 0)
                ),
        },
        {
            Header: 'QC Audit Units',
            accessor: 'qcUnits',
            Cell: ({ row, value }) =>
                loadingUnits ? (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={20} />
                    </div>
                ) : (
                    (row.original.qcUnits ?? 0)
                ),
        },
        {
            Header: 'Available Units',
            accessor: 'availableUnits',
            Cell: ({ row, value }) =>
                loadingUnits ? (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={20} />
                    </div>
                ) : (
                    (row.original.availableUnits ?? 0)
                ),
        },
    ];

    return (
        <Grid container>
            <Notifications options={notification} />
            <Grid item xs={12}>
                <FBMAccountPicker filterNonFCFBMAccounts />
            </Grid>
            {!!fbmAccount.id && (
                <>
                    <Grid item md={6} style={{ textAlign: 'left', paddingLeft: '40px' }}>
                        <h1>{fbmAccount.companyName} Inventory</h1>
                    </Grid>
                    <Grid
                        item
                        md={6}
                        style={{
                            textAlign: 'right',
                            paddingRight: '40px',
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <h2 style={{ margin: 0, marginRight: 8 }}>Facility</h2>
                            <Select
                                id="Facility_select"
                                style={{
                                    minWidth: 120,
                                    width: 'auto',
                                    marginLeft: 8,
                                    marginRight: 8,
                                }}
                                value={facilityID || ''}
                                variant="outlined"
                                onChange={(event) => {
                                    setFacilityID(event.target.value);
                                }}
                            >
                                {facilities.map((item) => (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.text}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        {loading ? (
                            <CircularProgress />
                        ) : (
                            <DataTable
                                editable
                                expandable
                                ExpansionComponent={<FBMItemLotDetails />}
                                csvExport
                                csvExportFileName="inventory"
                                columns={cols}
                                data={rows}
                                toolbarActions={[
                                    {
                                        name: 'Export Detailed',
                                        action: async () => {
                                            exportDetailedData();
                                            return false; // Don't clear selected rows
                                        },
                                        disabled: !rows || rows.length === 0,
                                    },
                                ]}
                                saveEdit={async ({ field, row, value }) => {
                                    const res = await api.updateItemInventoryRiskThreshold(
                                        fbmAccount.fbmAccountName,
                                        row.id,
                                        value,
                                        row.facilityID,
                                        row.type,
                                    );
                                    if (res.status === false) {
                                        setNotification({ text: 'Error updating Inventory', level: 'error' });
                                    } else {
                                        setNotification({ text: 'Inventory Updated!', level: 'success' });
                                        await getRows();
                                    }
                                    return res.status;
                                }}
                            />
                        )}
                    </Grid>
                </>
            )}
        </Grid>
    );
}

export { FBMInventory };
